import React, { useEffect } from 'react';
import { useSessionStore } from '../../stores/sessionStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { PokerTable } from './PokerTable';
import { ActionLog } from './ActionLog';
import { SessionControls } from './SessionControls';
import { ComboMatrix } from '../matrix/ComboMatrix';
import { SnapshotPanel } from '../matrix/SnapshotPanel';
import { TimerBar } from './TimerBar';

interface TrainingScreenProps {
  onShowdown: () => void;
}

export function TrainingScreen({ onShowdown }: TrainingScreenProps) {
  const phase = useSessionStore((s) => s.phase);
  const hand = useSessionStore((s) => s.hand);
  const actionIndex = useSessionStore((s) => s.actionIndex);
  const anonymized = useSettingsStore((s) => s.settings.anonymizeDefault);

  useEffect(() => {
    if (phase === 'SHOWDOWN') {
      onShowdown();
    }
  }, [phase]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Ignore if focused on an input/select/textarea
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return

      const store = useSessionStore.getState()
      if (store.phase === 'SHOWDOWN' || store.phase === 'IDLE' || store.phase === 'LOADING_HAND') return

      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault()
        if (store.phase === 'AWAITING_RANGE_UPDATE') {
          store.continueAfterRangeUpdate()
        } else {
          store.nextAction()
        }
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault()
        store.skipToShowdown()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, []);

  if (!hand) {
    return (
      <div className="training-loading">
        <div className="loading-spinner" />
        <p>Loading hand...</p>
      </div>
    );
  }

  // Build name→position map so ActionLog can show positions instead of player numbers
  const playerNameMap: Record<string, string> = {}
  for (const p of hand.players) {
    if (p.name && p.position) playerNameMap[p.name] = p.position
  }

  // Find target player's position
  const targetPlayer = hand.players.find(p => p.seat === hand.winner.seat)
  const targetPosition = targetPlayer?.position ?? 'Unknown'
  const currentStreet = useSessionStore((s) => s.currentStreet)

  // Effective stack in BB
  const bb = hand.bigBlind > 0 ? hand.bigBlind / 100 : 1
  const effectiveStack = targetPlayer ? Math.round(targetPlayer.stack / 100 / bb) : 0

  return (
    <div className="training-screen">
      {/* Left panel: history label + action log */}
      <div className="training-left">
        <div className="training-panel-header">History</div>

        {/* Target player info banner */}
        <div className="target-info-banner">
          <span className="target-position-badge">{targetPosition}</span>
          <span className="target-stack">{effectiveStack}bb effective</span>
        </div>

        <ActionLog
          actions={hand.actions}
          currentIndex={actionIndex}
          isAnonymized={anonymized}
          bigBlind={hand.bigBlind}
          playerNameMap={playerNameMap}
        />
      </div>

      {/* Center panel: poker table on top, controls pinned below */}
      <div className="training-center">
        {/* Skip — top-right of center panel */}
        {phase !== 'SHOWDOWN' && (
          <div className="training-skip-row">
            <button
              onClick={() => useSessionStore.getState().skipToShowdown()}
              className="skip-showdown-btn"
            >
              Skip to Showdown [S]
            </button>
          </div>
        )}

        <div className="training-table-area">
          <PokerTable
            hand={hand}
            actionIndex={actionIndex}
            isAnonymized={anonymized}
            showdownRevealed={phase === 'SHOWDOWN'}
          />
        </div>

        {/* Timer + range update prompt */}
        <TimerBar />
        {phase === 'AWAITING_RANGE_UPDATE' && (
          <div className="range-update-prompt">
            Update your range estimate for {targetPosition} on the {currentStreet}
          </div>
        )}

        <div className="training-controls-bar">
          <SessionControls />
        </div>
      </div>

      {/* Right panel: combo matrix + snapshot history */}
      <div className="training-right">
        <ComboMatrix />
        <SnapshotPanel />
      </div>
    </div>
  );
}
